// Vendored from cube-js/cube@d403e7a0 packages/cubejs-api-gateway/src/query.ts, Apache-2.0 (Copyright Cube Dev, Inc.).
// Changed: only the date normalization is kept, its comments are dropped, and `now` is passed through to dateParser.

import moment from "moment-timezone";

import { UserError } from "./user-error";
import { dateParser } from "./date-parser";

const DateRegex = /^\d\d\d\d-\d\d-\d\d$/;

const DATE_RANGE_OPERATORS = ["inDateRange", "notInDateRange"];
const START_DATE_OPERATORS = ["beforeDate", "afterOrOnDate"];
const END_DATE_OPERATORS = ["beforeOrOnDate", "afterDate"];

const AbsoluteDateTimeRegex =
  /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d{1,6})?)?(Z|[+-]\d{2}(:?\d{2})?)?)?$/;

export function resolveDateRange(
  input: string | string[],
  timezone: string,
  now: Date,
): string[];
export function resolveDateRange(
  input: unknown,
  timezone: string,
  now: Date,
): unknown;
export function resolveDateRange(
  input: unknown,
  timezone: string,
  now: Date,
): unknown {
  let dateRange: string[];
  if (typeof input === "string") {
    dateRange = dateParser(input, timezone, now);
  } else if (Array.isArray(input)) {
    dateRange = input.length === 1 ? [input[0], input[0]] : input;
  } else {
    return input;
  }

  return dateRange.map((d, i) =>
    i === 0
      ? moment
          .utc(d)
          .format(
            d.match(DateRegex)
              ? "YYYY-MM-DDT00:00:00.000"
              : moment.HTML5_FMT.DATETIME_LOCAL_MS,
          )
      : moment
          .utc(d)
          .format(
            d.match(DateRegex)
              ? "YYYY-MM-DDT23:59:59.999"
              : moment.HTML5_FMT.DATETIME_LOCAL_MS,
          ),
  );
}

export const normalizeDateFilterValues = <
  T extends { operator?: string; values?: unknown[] },
>(
  filter: T,
  timezone: string,
  now: Date,
): T => {
  if (!filter || !filter.operator || !Array.isArray(filter.values)) {
    return filter;
  }

  if (
    (DATE_RANGE_OPERATORS.includes(filter.operator) ||
      filter.operator === "onTheDate") &&
    filter.values.length > 1 &&
    filter.values.some(
      (v) => typeof v === "string" && !AbsoluteDateTimeRegex.test(v),
    )
  ) {
    throw new UserError(
      `Relative-date strings are only supported when \`values\` has a single element for operator \`${filter.operator}\`. Pass an absolute two-element [start, end] pair, or a single relative string like ["last 2 weeks"]. Got: ${JSON.stringify(filter.values)}`,
    );
  }

  if (filter.values.length !== 1) {
    return filter;
  }

  const value = filter.values[0];
  if (typeof value !== "string") {
    return filter;
  }

  if (
    DATE_RANGE_OPERATORS.includes(filter.operator) ||
    filter.operator === "onTheDate"
  ) {
    return { ...filter, values: resolveDateRange(value, timezone, now) };
  }

  if (AbsoluteDateTimeRegex.test(value)) {
    return filter;
  }

  if (START_DATE_OPERATORS.includes(filter.operator)) {
    const [start] = resolveDateRange(value, timezone, now);
    return { ...filter, values: [start] };
  }

  if (END_DATE_OPERATORS.includes(filter.operator)) {
    const [, end] = resolveDateRange(value, timezone, now);
    return { ...filter, values: [end] };
  }

  return filter;
};
